import { assign, isObject } from 'lodash-es'
import { createTestimony, deleteTestimony, getTestimonies, getTestimony, updateTestimony } from '../../services/testimonies.service'
import { indexArrayBy } from '../../utils'

const __DEV__ = process.env.NODE_ENV !== 'production'

export const baseTestimoniesState = {
  testimonies: [],
  testimony: null,
  /**
   * @type {Number}
   */
  pagination: {
    count: 0,
    pageSize: 10,
    currentPage: 1,
    nextPage: 2
  }
}

export default {
  namespaced: true,
  state: baseTestimoniesState,
  getters: {
    pageSize: state => state.pagination.pageSize,
    currentPage: state => state.pagination.currentPage,
    count: state => state.pagination.count,

    // Single testimony
    testimony: state => state.testimony,

    // Testimonies array
    testimoniesArray: state =>
      state.testimonies
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),

    // Testimonies object
    testimonies: state =>
      state.testimonies.length
        ? indexArrayBy(
          state.testimonies
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          , 'id')
        : {},

    // Gets a single testimony by id
    testimonyById: (_state, getters) => (id) => {
      const { testimonies } = getters
      if (!isObject(testimonies)) return undefined
      return testimonies[id]
    }
  },
  mutations: {
    /**
     * Sets pagination data
     */
    SET_PAGINATION_DATA (state, payload) {
      state.pagination = payload
    },
    /**
     * Sets array of testimonies to active state
     */
    SET_TESTIMONIES (state, payload) {
      state.testimonies = payload.TestimoniesCollection.rows
      state.pagination.count = payload.TestimoniesCollection.count
    },
    /**
     * Sets specific testimony to active state
     */
    SET_TESTIMONY (state, payload) {
      state.testimony = payload
    },
    /**
     * Adds new testimony to active state
     */
    ADD_TESTIMONY (state, payload) {
      const collection = state.testimonies
      collection && Array.isArray(collection)
        ? collection.push(payload)
        : state.testimonies = [payload]
    },
    /**
     * Updates specific testimonies's object state
     */
    UPDATE_TESTIMONY (state, payload) {
      const targetIndex = state.testimonies.findIndex(testimony => testimony.id === payload.id)
      assign(state.testimonies[targetIndex], payload)
    },
    /**
     * Deletes specific testimony from state
     */
    DELETE_TESTIMONY (state, id) {
      const targetIndex = state.testimonies.findIndex(testimony => testimony.id === id)
      state.testimonies.splice(targetIndex, 1)
    },
    /**
     * Resets the original state for testimonies
     */
    RESET_TESTIMONIES (state) {
      assign(state, baseTestimoniesState)
    }
  },
  actions: {
    /**
     * Gets all testimonies
     */
    async getTestimonies ({ commit, rootState }, { pageSize = 10, page }) {
      const authToken = rootState.auth.authToken
      const response = await getTestimonies(authToken, { pageSize, page })
      const testimonies = response.data
      commit('SET_TESTIMONIES', testimonies.data)

      const { nextPage, currentPage } = testimonies.data

      commit('SET_PAGINATION_DATA', {
        pageSize: currentPage.pageSize,
        currentPage: currentPage.page,
        nextPage: nextPage.page,
        count: testimonies.data.TestimoniesCollection.count
      })
    },
    /**
     * Gets all testimonies
     */
    async getAllTestimonies ({ commit, rootState }) {
      const authToken = rootState.auth.authToken
      const response = await getTestimonies(authToken)
      const testimonies = response.data
      commit('SET_TESTIMONIES', testimonies.data)
    },
    /**
     * Gets a single testimony
     */
    async getTestimony ({ commit, rootState }, id) {
      const authToken = rootState.auth.authToken
      const response = await getTestimony(authToken, id)
      const testimony = response.data.data
      commit('SET_TESTIMONY', testimony)
    },
    /**
     * Creates a new testimony
     */
    async createTestimony ({ commit }, payload) {
      const response = await createTestimony(payload)
      const testimony = response.data
      commit('ADD_TESTIMONY', testimony.data)
    },
    /**
     * Updates a testimony
     */
    async updateTestimony ({ commit, rootState }, { id, payload }) {
      const authToken = rootState.auth.authToken
      const response = await updateTestimony(authToken, id, payload)
      const [testimony] = response.data.data
      commit('UPDATE_TESTIMONY', testimony)
      commit('SET_TESTIMONY', testimony)
    },
    /**
     * Deletes a testimony
     */
    async deleteTestimony ({ commit, rootState }, id) {
      const authToken = rootState.auth.authToken
      const response = await deleteTestimony(authToken, id)
      const { message } = response
      __DEV__ && console.log(`Deleted testimony of id: ${id}:`, message)
      commit('DELETE_TESTIMONY', id)
    }
  }
}
